<template>
    <div>
        <div v-if="collection.hasItems">
            <div v-for="(item, index) in items">
                <div class="grid-content align-items-start" :class="outerGridClass">
                    <div class="grid-item" :class="itemFilterClass">
                        <slot
                            name="filter-selector"
                            :is-criteria="isCriteria"
                            :filter-set="filterSet"
                            :filter-options="filterOptions"
                            :select-filter="selectFilter"
                            :item="item"
                            :index="index">
                            <v-select
                                :value="item.pointer"
                                @input="selectFilter($event, index, item)"
                                placeholder="Select filter"
                                :options="filterOptions(item)"/>
                        </slot>
                    </div>
                    <div class="grid-item" :class="itemOperatorClass">
                        <slot
                            name="filter-operator"
                            :update-line-operator="updateLineOperator"
                            :index="index"
                            :item="item">
                            <operator
                                :key="item.pointer"
                                :item="item"
                                @update="updateLineOperator($event, index, item)"/>
                        </slot>
                    </div>
                    <div class="grid-item">
                        <slot
                            name="filter-data"
                            :item="item"
                            :index="index"
                            :update-line-values="updateLineValues">
                            <value-editor
                                :key="item.$id"
                                :item="item"
                                :validate="$v.collection.items[index]"
                                @update="updateLineValues($event, index, item)"
                            />
                        </slot>
                    </div>
                    <div class="grid-item no-flex">
                        <slot
                            v-if="allowDeletion"
                            name="remove-filter"
                            :remove-filter="removeFilter"
                            :filter-index="index">
                            <v-icon-button type="basic" @click="removeFilter(index)">
                                <v-icon icon="close"/>
                            </v-icon-button>
                        </slot>
                    </div>
                </div>
                <div class="divider" v-if="useSeparators && isSeparatedLine(index)"></div>
            </div>
        </div>
        <div class="grid-fitted margin-top-m">
            <slot
                v-if="collection.hasItems"
                name="add-filter"
                :add-filter="addFilter">
                <div class="grid-item">
                    <r-button size="small" @click.prevent="addFilter">Add Filter</r-button>
                </div>
            </slot>
            <slot
                v-else
                name="no-items"
                :add-filter="addFilter">
                <div class="grid-item">
                    <r-button size="small" @click.prevent="addFilter">Add Filter to Begin</r-button>
                </div>
            </slot>
        </div>
    </div>
</template>

<script>
    import {
        vIconButton, vIcon, vSelect,
    } from '@/components/form';
    import QueryCollection from '@/components/query2/query-collection';
    import QueryCollectionValidator from '@/components/query2/query-collection-validator';
    import FilterOptionsBuilder from '@/components/query2/filter-options-builder';

    const Operator = () => import(/* webpackChunkName: "query2" */'@/components/query2/components/operator');
    const ValueEditor = () => import(/* webpackChunkName: "query2" */'@/components/query2/components/value-editor');

    export default {
        name: 'query2',
        components: {
            vIconButton,
            vIcon,
            vSelect,
            Operator,
            ValueEditor,
        },
        props: {
            isCriteria: {
                type: Boolean,
                default: false,
            },
            query: [Array, Object],
            filterSet: Object,
            /**
             * Define whether the deletion button is visible. True by default.
             * @type {boolean}
             */
            allowDeletion: {
                type: Boolean,
                default: true,
            },
            /**
             * Define whether the validation happens immediately as the values are changed by the
             * user.
             * @type {boolean}
             */
            immediateValidation: {
                type: Boolean,
                default: true,
            },
            /**
             * Define whether the component needs to fit in a compact space,
             * usually a column within a form.
             * @type {boolean}
             */
            compact: Boolean,
            /**
             * Define whether to render line separators between each condition item.
             * @type {boolean}
             */
            useSeparators: Boolean,
        },
        watch: {
            query(selectedTabData) {
                // TODO remove once tabs are deprecated in grids
                this.collection.populate(selectedTabData);
            },
        },
        computed: {
            items() {
                return this.collection.items;
            },
            hasInvalidItems() {
                return this.$v.$error;
            },
            outerGridClass() {
                return this.compact
                    ? ''
                    : 'grid-query-large';
            },
            itemFilterClass() {
                return this.compact
                    ? 'grid-item-half grid-item-break-half'
                    : 'no-flex grid-item-query-filter';
            },
            itemOperatorClass() {
                return this.compact
                    ? 'grid-item-quarter'
                    : 'no-flex grid-item-query-operator';
            },
        },
        data() {
            return {
                collection: new QueryCollection(),
                isReady: false,
                validatorCache: {collection: {items: {}}},
            };
        },
        validations() {
            return this.validatorCache;
        },
        methods: {
            initCollection() {
                this.collection = new QueryCollection({
                    query: this.query,
                    isCriteria: this.isCriteria,
                    filterSet: this.filterSet,
                });
                // immediately update parent with parsed values
                this.dispatchUpdate();
                if (this.immediateValidation) {
                    this.$v.$touch();
                }
            },
            /**
             * Generate options for the pointer selection menu based on what was previously
             * selected.
             * @param {string} pointer
             * @returns {{label: string, value: string}[]}
             */
            filterOptions({pointer}) {
                const {selectedPointers} = this.collection;
                return this.isReady
                    ? new FilterOptionsBuilder(this.filterSet)
                        .build({
                            selectedPointers,
                            pointer,
                        })
                    : [];
            },
            addFilter() {
                this.collection.addItem();
            },
            removeFilter(index) {
                this.collection.removeItem(index);
                this.dispatchUpdate();
            },
            /**
             * Updates the line to set the type for the pointer and emits
             * the event upwards.
             * @param {string} pointer
             * @param {number} index
             * @param {QueryLine} item
             */
            selectFilter(pointer, index, item) {
                if (item.isBlank) {
                    item.setNewlyAdded();
                }
                item.updateFilter({
                    filterSet: this.filterSet,
                    pointer,
                });
                this.forceRebuildValidators();
                this.$emit('item-filter-selected', {
                    pointer,
                    index,
                    item,
                });
                // don't dispatch immediately on a new filter line
                if (!item.isNewlyAdded) {
                    this.dispatchUpdate();
                } else {
                    item.setIsDirty();
                }
            },
            /**
             * Update the operator for the query line at the specified index.
             * @param {string} operator
             * @param {number} index
             * @param {QueryLine} item
             */
            updateLineOperator({operator}, index, item) {
                item.setOperator(operator);
                this.$emit('item-operator-selected', {
                    item,
                    operator,
                    index,
                });
                this.dispatchUpdate();
            },
            /**
             * Set the values for the query line at the specified index. Rebuild validators and
             * trigger the validation if required.
             * @param {?[]} values
             * @param {number} index
             * @param {QueryLine} item
             */
            updateLineValues(values, index, item) {
                // validation will not trigger when the component
                // renders unless required
                item.setValues(values);
                this.forceRebuildValidators();
                this.validate();
                if (!this.hasInvalidItems) {
                    this.$emit('item-values-updated', {
                        values,
                        index,
                        item,
                    });
                    this.dispatchUpdate();
                }
            },
            /**
             * Force the rebuild of validators after query items were mutated. This is required
             * because Vuelidate will not detect deep object modifications without explicitly using
             * Vue.$set.
             */
            forceRebuildValidators() {
                // update cache to force re-computation of validators by vuelidate
                const items = new QueryCollectionValidator({
                    items: this.items,
                    timezone: this.$tz(),
                }).getValidators();
                this.validatorCache = {collection: {items}};
            },
            /**
             * Dispatch a parent update with the collection payload.
             */
            dispatchUpdate() {
                this.$emit('update', this.collection.getPayload());
            },
            /**
             * Trigger the vuelidate validation.
             */
            validate() {
                this.$v.$touch();
                this.$emit('validation', {
                    validate: this.$v.collection,
                    hasInvalidItems: this.hasInvalidItems,
                });
            },
            isSeparatedLine(index) {
                return index !== this.items.length - 1;
            },
        },
        mounted() {
            if (this.isCriteria) {
                // TODO
                this.initCollection();
            } else {
                (async () => {
                    await this.filterSet.getFilters();
                    this.isReady = true;
                    this.initCollection();
                    this.$emit('ready');
                })();
            }
        },
    };
</script>
